import { Injectable } from '@angular/core'
import { Angulartics2Facebook, Angulartics2GoogleGlobalSiteTag } from 'angulartics2'
import { filter, take, tap } from 'rxjs/operators'
import {
  AvacyCookieBannerService,
  FACEBOOK_VENDOR_ID,
  GOOGLE_ADV_VENDOR_ID,
} from '../cookie-banner/avacy-cookie-banner.service'
import { appConfig } from '../../../brands/_current/config/app-config'
import { SSO_RETURN_URL_PARAM } from '../../login/shared/login-query-params'

export enum AnalyticsEventType {
  facebook,
  googleGlobalSiteTag,
}

export const FORCED_ANALYTICS_CONSENT_QUERY_PARAM = 'ac'

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  /**
   * Tri-state property based on FORCED_ANALYTICS_CONSENT_QUERY_PARAM.
   * If true, the user accepts all analytics condition and bypasses the standard cookie acceptance flow.
   * If false, the user denies all analytics condition and bypasses the standard cookie acceptance flow.
   * If null, the standard cookie acceptance flow is active.
   */
  readonly forcedAnalyticsConsent: boolean | null

  constructor(
    private readonly angulartics2GoogleGlobalSiteTag: Angulartics2GoogleGlobalSiteTag,
    private readonly angulartics2Facebook: Angulartics2Facebook,
    private readonly avacyService: AvacyCookieBannerService,
  ) {
    try {
      const returnUrl = decodeURIComponent(new URLSearchParams(window.location.search).get(SSO_RETURN_URL_PARAM) ?? '')
      const analyticsConsentFromQueryString = JSON.parse(
        new URLSearchParams(window.location.search).get(FORCED_ANALYTICS_CONSENT_QUERY_PARAM) ?? null,
      )
      const analyticsConsentFromReturnUrl = JSON.parse(
        new URLSearchParams(new URL(returnUrl).search).get(FORCED_ANALYTICS_CONSENT_QUERY_PARAM) ?? null,
      )
      this.forcedAnalyticsConsent = analyticsConsentFromQueryString ?? analyticsConsentFromReturnUrl
    } catch (e) {
      this.forcedAnalyticsConsent = null
      console.error(e)
    }
  }

  /**
   * Tracks an analytics event for the specified type and action. If cookie consent is required, waits for user preferences before tracking.
   * @param type The analytics event type (facebook or googleGlobalSiteTag).
   * @param action The action name for the event.
   * @param properties Optional properties for the event.
   */
  eventTrack(type: AnalyticsEventType, action: string, properties?: Record<string, unknown>) {
    if (this.forcedAnalyticsConsent !== false) {
      if (appConfig.cookieBanner.avacyCookieBanner && !this.forcedAnalyticsConsent) {
        this.avacyService.avacyPreferences$
          .pipe(
            filter((avacyPreferences) => {
              switch (type) {
                case AnalyticsEventType.facebook:
                  return avacyPreferences?.customVendorIds.includes(FACEBOOK_VENDOR_ID) ?? false
                case AnalyticsEventType.googleGlobalSiteTag:
                  return avacyPreferences?.iabVendorsIds.includes(GOOGLE_ADV_VENDOR_ID) ?? false
                default:
                  return true
              }
            }),
            take(1),
            tap(() => this.eventTrackBasedOnType(type, action, properties)),
          )
          .subscribe()
      } else {
        this.eventTrackBasedOnType(type, action, properties)
      }
    }
  }

  /**
   * Tracks the event based on the analytics type, sending it to the correct provider.
   * @param type The analytics event type.
   * @param action The action name for the event.
   * @param properties Optional properties for the event.
   */
  private eventTrackBasedOnType(type: AnalyticsEventType, action: string, properties?: Record<string, unknown>) {
    switch (type) {
      case AnalyticsEventType.facebook:
        this.angulartics2Facebook.eventTrack(action, properties)
        break
      case AnalyticsEventType.googleGlobalSiteTag:
        this.angulartics2GoogleGlobalSiteTag.eventTrack(action, properties)
        break
    }
  }
}
